
import React, { useState, useEffect } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { formatCurrency } from '@/components/borrowings/debt/utils';
import { useToast } from "@/hooks/use-toast";
import { Skeleton } from "@/components/ui/skeleton";
import LazyLoad from "@/components/LazyLoad";

interface BorrowingSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  amount: number;
  timeHorizon: string;
}

export const BorrowingSheet = ({ open, onOpenChange, amount, timeHorizon }: BorrowingSheetProps) => {
  const [selectedInstrument, setSelectedInstrument] = React.useState("wcl");
  const [borrowAmount, setBorrowAmount] = React.useState(Math.abs(amount).toString());
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  // Reset loading state when sheet opens
  useEffect(() => {
    if (open) {
      setIsLoading(true);
      const timer = setTimeout(() => {
        setIsLoading(false);
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [open]);

  const handleBorrow = () => {
    toast({
      title: "Borrowing Initiated",
      description: `₹${(parseFloat(borrowAmount) / 10000000).toFixed(2)} Cr borrowed as ${getInstrumentLabel(selectedInstrument)} for ${timeHorizon} horizon`,
    });
    onOpenChange(false);
  };

  const getInstrumentLabel = (instrument: string) => {
    switch(instrument) {
      case "wcl": return "Working Capital Loan";
      case "tl": return "Term Loan";
      case "bond": return "Corporate Bond";
      case "cp": return "Commercial Paper";
      default: return instrument;
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>Arrange Funding</SheetTitle>
          <SheetDescription>
            Address your cash deficit of {formatCurrency(Math.abs(amount))} for {timeHorizon} horizon
          </SheetDescription>
        </SheetHeader>

        {isLoading ? (
          <div className="w-full animate-pulse mt-6">
            <Skeleton className="h-8 w-full mb-4" />
            <Skeleton className="h-8 w-full mb-4" />
            <Skeleton className="h-8 w-full mb-4" />
          </div>
        ) : (
          <LazyLoad delay={100}>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="amount">Borrow Amount</Label>
                <Input
                  id="amount"
                  value={borrowAmount}
                  onChange={(e) => setBorrowAmount(e.target.value)}
                  className="col-span-3"
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="instrument">Borrowing Instrument</Label>
                <Select value={selectedInstrument} onValueChange={setSelectedInstrument}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select instrument" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="wcl">Working Capital Loan</SelectItem>
                    <SelectItem value="tl">Term Loan</SelectItem>
                    <SelectItem value="bond">Corporate Bond</SelectItem>
                    <SelectItem value="cp">Commercial Paper</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="grid gap-2">
                <Label>Estimated Interest Rate</Label>
                <div className="text-sm p-2 border rounded bg-muted/30">
                  {selectedInstrument === "wcl" && "8.5% - 10.0% p.a."}
                  {selectedInstrument === "tl" && "7.8% - 9.5% p.a."}
                  {selectedInstrument === "bond" && "7.5% - 9.0% p.a."}
                  {selectedInstrument === "cp" && "6.5% - 8.0% p.a."}
                </div>
              </div>
            </div>
          </LazyLoad>
        )}

        <SheetFooter className="mt-4">
          <Button onClick={handleBorrow} disabled={isLoading}>Arrange Funding</Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};
